import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { 
  Crown,
  Eye,
  EyeOff,
  ArrowLeft,
  AlertCircle,
  CheckCircle
} from 'lucide-react'
import { Button } from '../components/ui/button'
import GlassCard from '../components/GlassCard'
import Starfield from '../components/Starfield'
import { supabase } from '../services/supabase'



const Register = () => {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [error, setError] = useState<string>('')
  const [success, setSuccess] = useState<string>('')

  const [formData, setFormData] = useState({
    commanderName: 'Crownofsuns',
    email: 'janandrerostad@gmail.com',
    password: '',
    confirmPassword: '',
    agreeToTerms: false
  })

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    setError('')
  }

  const validateForm = () => {
    if (!formData.commanderName.trim()) {
      setError('Commander name is required')
      return false
    }
    if (formData.commanderName.length < 3) {
      setError('Commander name must be at least 3 characters')
      return false
    }
    if (!formData.email.trim()) {
      setError('Email address is required')
      return false
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address')
      return false
    }
    if (!formData.password) {
      setError('Password is required')
      return false
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters')
      return false
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match')
      return false
    }
    if (!formData.agreeToTerms) {
      setError('You must agree to the Terms of Service and Privacy Policy')
      return false
    }
    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) return

    setIsLoading(true)
    setError('')

    try {
      // Check if we have valid Supabase credentials
      const hasValidCredentials = !import.meta.env.VITE_SUPABASE_ANON_KEY?.includes('placeholder')
      
      if (!hasValidCredentials) {
        // Development mode: simulate successful registration
        console.log('Demo mode: Simulating successful registration')
        setSuccess('🚀 Welcome to Crown of Suns! Registration successful. Redirecting to your empire dashboard...')
        
        // Simulate delay and redirect
        setTimeout(() => {
          navigate('/dashboard')
        }, 2000)
        return
      }

      // Attempt real Supabase registration
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            username: formData.commanderName
          }
        }
      })

      if (authError) {
        console.error('Authentication error:', authError)
        if (authError.message.includes('Forbidden use of secret API key') || authError.message.includes('secret API key in browser')) {
          // Fall back to demo mode when API keys are misconfigured
          console.log('API key configuration issue, falling back to demo mode')
          setSuccess('🚀 Welcome to Crown of Suns! Registration successful in demo mode. Redirecting to your empire dashboard...')
          setTimeout(() => {
            navigate('/dashboard')
          }, 2000)
          return
        } else if (authError.message.includes('Invalid API key') || authError.message.includes('not authorized')) {
          setError('🔑 Authentication system is being configured. Please try again in a few moments or contact support if the issue persists.')
        } else if (authError.message.includes('User already registered')) {
          setError('An account with this email already exists. Please try signing in instead.')
        } else if (authError.message.includes('weak password')) {
          setError('Password is too weak. Please use a stronger password with at least 8 characters.')
        } else {
          setError(`Registration failed: ${authError.message}`)
        }
        return
      }

      if (authData.user) {
        // Profile is created automatically by database trigger
        // Skip email confirmation check for development
        setSuccess('🚀 Empire created successfully! Welcome, Commander. Redirecting to your empire dashboard...')
        setTimeout(() => {
          navigate('/dashboard')
        }, 2000)
      }
    } catch (err: any) {
      console.error('Registration error:', err)
      setError('An unexpected error occurred. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }



  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0a] via-[#1a1a2e] to-[#16213e] relative overflow-hidden">
      <Starfield density={400} speed={0.6} />
      
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <Crown className="w-12 h-12 text-yellow-400 mr-3" />
              <h1 className="text-4xl font-bold text-white">Join the Empire</h1>
            </div>
            <p className="text-gray-300 text-lg">Create your account and begin your cosmic conquest</p>
          </div>

          {/* Error/Success Messages */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <span className="text-red-400 text-sm">{error}</span>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
              <span className="text-green-400 text-sm">{success}</span>
            </div>
          )}

          {/* Registration Form */}
          <GlassCard variant="premium" className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Commander Name */}
              <div className="space-y-2">
                <label htmlFor="commanderName" className="block text-sm font-medium text-white">
                  Commander Name
                </label>
                <input
                  id="commanderName"
                  name="commanderName"
                  type="text"
                  value={formData.commanderName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('commanderName', e.target.value)}
                  className="w-full bg-black/30 border border-white/20 rounded-lg px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400/50"
                  placeholder="Enter your commander name"
                  autoComplete="username"
                  required
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <label htmlFor="email" className="block text-sm font-medium text-white">
                  Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('email', e.target.value)}
                  className="w-full bg-black/30 border border-white/20 rounded-lg px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400/50"
                  placeholder="Enter your email"
                  autoComplete="email"
                  required
                />
              </div>



              {/* Password */}
              <div className="space-y-2">
                <label htmlFor="password" className="block text-sm font-medium text-white">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('password', e.target.value)}
                    className="w-full bg-black/30 border border-white/20 rounded-lg px-3 py-2 pr-12 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400/50"
                    placeholder="Enter your password"
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div className="space-y-2">
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-white">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('confirmPassword', e.target.value)}
                    className="w-full bg-black/30 border border-white/20 rounded-lg px-3 py-2 pr-12 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400/50"
                    placeholder="Confirm your password"
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Terms Agreement */}
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="terms"
                  checked={formData.agreeToTerms}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('agreeToTerms', e.target.checked)}
                  className="mt-1 w-4 h-4 bg-black/30 border-white/20 rounded focus:ring-blue-400/20 focus:ring-2 text-blue-400"
                />
                <label htmlFor="terms" className="text-sm text-gray-300 leading-relaxed">
                  I agree to the{' '}
                  <Link to="/terms" className="text-blue-400 hover:text-blue-300 underline">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy" className="text-blue-400 hover:text-blue-300 underline">
                    Privacy Policy
                  </Link>
                </label>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Creating Empire...
                  </div>
                ) : (
                  'Create Empire'
                )}
              </Button>
            </form>

            {/* Login Link */}
            <div className="mt-6 text-center">
              <p className="text-gray-400">
                Already have an account?{' '}
                <Link to="/login" className="text-blue-400 hover:text-blue-300 underline font-medium">
                  Sign in here
                </Link>
              </p>
            </div>
          </GlassCard>

          {/* Back to Home */}
          <div className="mt-6 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </div>
      
    </div>
  )
}

export default Register